import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ApiResponse, ApiPayload, SearchConfig, FETCH_METHODS, API_STATUS, ExportConfig } from '../model/api.model';
import { ApiService } from './api/api.service';
import { ExcelExportService } from './excel-export.service';
import FileSaver from 'file-saver';
import { CommonUtils } from '../utils/common.utils';
import { DynamicFormArray } from '../model/form.model';
import { DownloadApiConfig, exportConfig, TableColumnSchema } from '../model/dynamic-data-table.model';
import { SpinnerArea } from '../model/common.model';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  excelService = inject(ExcelExportService);
  public loggedUserDetails!:any;
  private readonly commonUtils = inject(CommonUtils);
  public isTenureAdmin = false;
  public isAdmin = false;
  private readonly apiService = inject(ApiService);
  constructor() { }

  getDataFromDataSource(params: SearchConfig, baseUrl: string): Promise<ApiResponse> {
    const env = (globalThis as any).environment;
    if (!env?.[baseUrl]) {
      console.info(`Environment key '${baseUrl}' missing. Returning empty response.`);
      return Promise.resolve({ status: API_STATUS.Error, result: [], message: `Missing environment key: ${baseUrl}` } as ApiResponse);
    }
    const url = `${env[baseUrl]}${params['path']}`;
    const apiPayLoad: ApiPayload = {
      url: url,
      method: params['method'],
      body: params['body']
    }
    if(params['headers']){
      apiPayLoad['headers'] = params['headers']
    }
    return lastValueFrom(this.apiService.fetchAllData(apiPayLoad))
  }
  getDataFromS3Bucket(bucketName:string, fileName:string, url: string){
    const body = {
      bucketName: bucketName,
      key: fileName,
      type: 'Download'
    }
    const params:ApiPayload = {
      url: url+ '/getSignedUrl',
      method: FETCH_METHODS['Post'],
      body: body
    }
    return lastValueFrom(this.apiService.fetchAllData(params))
  }
  recursiveCallS3(s3Config: any, exportConfig: ExportConfig) {
    return new Promise((resolve) => {
      const intervalId = setInterval(() => {
        const config: any = {
          bucket: s3Config['bucketName'],
          fileName: s3Config['fileName']
        }
        return this.downloadExcelFromS3(config, exportConfig, intervalId);
      }, 10000); // every 30 seconds
      // Optional: Stop after 2 minutes regardless
      setTimeout(() => {
        clearInterval(intervalId);
        console.info('Stopped polling after 2 minutes.');
        return resolve({})
      }, 300000); // 5 minutes
    })
  }
  downloadExcelFromS3(s3Config: { bucket: string, fileName: string, url: string, displayName?: string },exportConfig:ExportConfig, intervalId?:any) {
  if(exportConfig){
    this.commonUtils.spinner('show');
    const searchConfig = structuredClone(exportConfig.searchParameters);
    searchConfig['body']['bucketName'] = s3Config['bucket']
    searchConfig['body']['key'] = s3Config['fileName']
    return this.getDataFromDataSource(searchConfig, exportConfig.environment).then(response => {
   
      if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result) && response['result'][0]) {
        clearInterval(intervalId);
        const signedUrl = response['result'][0]['SignedURL'];
        let fileName = s3Config.fileName.includes('/') ? s3Config.fileName.split('/').pop() : s3Config.fileName;  
        if(s3Config?.displayName){
          fileName = s3Config.displayName
        }
        return fetch(signedUrl)
              .then(res => res.blob())
              .then(blob => {
                FileSaver.saveAs(blob, fileName ?? '');
                this.commonUtils.showMatSnackBar('File Exported Successfully');
                      this.commonUtils.spinner('hide');
              })
              .catch(err => {
                this.commonUtils.spinner('hide');
                console.info('Error downloading image file:', err); 
              });

        } else {
      this.commonUtils.spinner('hide');
       console.info('Error downloading file')
      }
      return Promise.resolve(); 
    }).catch(err=>{
      this.commonUtils.spinner('hide');
      this.commonUtils.showMatSnackBar('Error Downloading Data');
      console.info('Error downloading', err);
    }).finally(() => {
      this.commonUtils.spinner('hide');
    })
  }
  this.commonUtils.spinner('hide');
  return Promise.resolve()
 }
 
  async exportUsingDownloadApi(downloadApiConfig?: DownloadApiConfig,filterSchema?: DynamicFormArray,tableExportConfig?: exportConfig | null,spinnerName?: SpinnerArea): Promise<boolean> {
    if (!downloadApiConfig?.path || !downloadApiConfig?.urlPath || !downloadApiConfig?.method) {
      return false;
    }

    const body = { ...structuredClone(downloadApiConfig.body) };
    filterSchema?.FieldsArray?.forEach(field => {
      const slctedVaue = field?.selectedValue;
      if (slctedVaue !== '' && slctedVaue !== null && slctedVaue !== undefined) {
        body[field.fieldName] = slctedVaue;
      }
    });
    this.commonUtils.spinner('show', spinnerName);
    try {
      const response = await this.getDataFromDataSource({ ...structuredClone(downloadApiConfig), body }, downloadApiConfig.urlPath);
      const rows = Array.isArray(response.result) ? response.result : [];
      const success = response.status === API_STATUS.Success && rows.length > 0 && !!tableExportConfig?.schema;
      if (!success) {
        this.commonUtils.showMatSnackBar('Export Failed - No Data Found');
      } else if (tableExportConfig?.groupedSheets?.useResponseKeys) {
        const responseObj = rows[0] ?? {};
        const excludeKeys = tableExportConfig.groupedSheets.excludeKeys ?? [];
        const schemaMap = tableExportConfig.groupedSheets.schemaMap ?? {};
        const dataset: Record<string, any[]> = {};
        Object.entries(responseObj).forEach(([key, sheetRows]) => {
          if (!excludeKeys.includes(key) && Array.isArray(sheetRows)) {
            const sheetConfig = schemaMap[key]
              ? { ...tableExportConfig, schema: schemaMap[key] }
              : tableExportConfig;
            dataset[key] = structuredClone(this.commonUtils.formatExcelData(sheetConfig, sheetRows, filterSchema)) ?? [];
          }
        });
        this.excelService.exportToMultipleSheets(dataset, tableExportConfig.fileName ?? '');
      } 
      else if (tableExportConfig?.groupedSheets) {
        const dataset = this.buildGroupedSheetDataset(tableExportConfig, rows, filterSchema);
        this.excelService.exportToMultipleSheets(dataset, tableExportConfig.fileName ?? '');
      } 
      else if (tableExportConfig) {
        const data = structuredClone(this.commonUtils.formatExcelData(tableExportConfig, rows, filterSchema)) ?? [];
        this.excelService.exportAsExcelFile(data, tableExportConfig.fileName ?? '', tableExportConfig.descriptions ?? []);
      }
    } catch (err) {
      console.info('Export API error:', err);
      this.commonUtils.showMatSnackBar('Error downloading data');
    } finally {
      this.commonUtils.spinner('hide', spinnerName);
    }
    return true;
  }

  private deduplicateRows(rows: any[]): any[] {
    const seen = new Set<string>();
    return rows.filter(row => {
      const key = JSON.stringify(row);
      if (seen.has(key)) { return false; }
      seen.add(key);
      return true;
    });
  }

  buildGroupedSheetDataset(tableExportConfig: exportConfig, rows: any[], filterSchema?: DynamicFormArray): Record<string, any[]> {
    const dataset: Record<string, any[]> = {};
    if (!tableExportConfig.groupedSheets) { return dataset; }
    const { groupByField = '', includeOriginalSheet = true } = tableExportConfig.groupedSheets;
    const uniqueRows = this.deduplicateRows(rows);
    if (includeOriginalSheet) {
      dataset['data'] = structuredClone(this.commonUtils.formatExcelData(tableExportConfig, uniqueRows, filterSchema)) ?? [];
    }

    const grouped = uniqueRows.reduce<Record<string, any[]>>((acc, row: any) => {
      const key = (row[groupByField] ?? 'Unknown').toString();
      if (!acc[key]){ acc[key] = []};
      acc[key].push(row);
      return acc;
    }, {});

    Object.entries(grouped).forEach(([sheetName, groupRows]) => {
      dataset[sheetName] = structuredClone(this.commonUtils.formatExcelData(tableExportConfig, groupRows, filterSchema)) ?? [];
    });

    return dataset;
  }

  applyColumnMergesBySpinId(rows: any[], columnSchema: TableColumnSchema[]): any[] {
    const mergeColumns = (columnSchema ?? [])
        .filter(col => col.isColumnsMerge)
        .map(col => col.fieldKey);

    if (!Array.isArray(rows) || mergeColumns.length <= 0) {
        return rows;
    }

    const data = structuredClone(rows);
    let mergeGroupCounter = 0;

    mergeColumns.forEach((columnKey) => {
        let index = 0;
        while (index < data.length) {
            const groupStart = index;
            const groupId = data[index]?.spinId;
            index += 1;

            if (groupId === undefined || groupId === null || groupId === '') {
                continue;
            }

            while (index < data.length && data[index]?.spinId === groupId) {
                index += 1;
            }

            const groupEnd = index;
            const groupSize = groupEnd - groupStart;
            if (groupSize <= 1) {
                continue;
            }

            const mergedValue = data[groupStart]?.[columnKey] ?? '';
            const groupIdentifier = `merge-group-${mergeGroupCounter}`;
            mergeGroupCounter++;

            data[groupStart].__rowSpan ??= {};
            data[groupStart].__rowSpan[columnKey] = groupSize;
            data[groupStart][columnKey] = mergedValue;
            data[groupStart].__mergeGroupId = groupIdentifier;

            for (let i = groupStart + 1; i < groupEnd; i += 1) {
                data[i].__hiddenColumns ??= [];
                if (!data[i].__hiddenColumns.includes(columnKey)) {
                    data[i].__hiddenColumns.push(columnKey);
                }
                data[i][columnKey] = '';
                data[i].__mergeGroupId = groupIdentifier;
            }
        }
    });

    return data;
  }
}
